import { API_BASE_URL } from '../config';
import { authFetch } from './config';
import type {
  ExplorerTablesResponse,
  TableSchemaResponse,
  ExplorerQueryRequest,
  ExplorerQueryResponse,
  ExplorerExportRequest,
  ExplorerProfileRequest,
  ExplorerProfileResponse,
  ExplorerXerScheduleProject,
} from '../types/explorer';

async function explorerFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await authFetch(`${API_BASE_URL}/explorer${path}`, {
    ...options,
    credentials: 'include',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Explorer API error: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getExplorerTables(): Promise<ExplorerTablesResponse> {
  return explorerFetch<ExplorerTablesResponse>('/tables');
}

export async function getExplorerTableSchema(
  tableId: string
): Promise<TableSchemaResponse> {
  return explorerFetch<TableSchemaResponse>(`/tables/${encodeURIComponent(tableId)}/schema`);
}

export async function postExplorerQuery(
  payload: ExplorerQueryRequest
): Promise<ExplorerQueryResponse> {
  return explorerFetch<ExplorerQueryResponse>('/query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/**
 * Export current filtered dataset as CSV. Returns blob for download.
 * Caller should trigger download (e.g. createObjectURL + link.click).
 */
export async function postExplorerExport(
  payload: ExplorerExportRequest
): Promise<Blob> {
  const response = await authFetch(`${API_BASE_URL}/explorer/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    credentials: 'include',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Export failed: ${response.status}`);
  }
  return response.blob();
}

export async function postExplorerProfile(
  payload: ExplorerProfileRequest
): Promise<ExplorerProfileResponse> {
  return explorerFetch<ExplorerProfileResponse>('/profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function getExplorerXerScheduleProjects(
  projectId: string,
  importRunId?: string
): Promise<{ import_run_id: string; projects: ExplorerXerScheduleProject[] }> {
  const qs = importRunId
    ? `?version_id=${encodeURIComponent(importRunId)}`
    : '';
  const response = await authFetch(
    `${API_BASE_URL}/v1/projects/${encodeURIComponent(projectId)}/xer-schedule-projects${qs}`,
    {
      credentials: 'include',
    }
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Explorer schedule projects failed: ${response.status}`);
  }
  return response.json();
}
